import { defineStore } from 'pinia'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import { computed, ref } from 'vue'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import PaginationRequest from '@/core/domain/pagination/PaginationRequest.ts'
import { isUserAdminView, type UserAdminView } from '@/core/domain/users/LocalUser.ts'
import FetchAllUserUseCase from '@/core/useCases/users/FetchAllUserUseCase.ts'
import FetchUserByIdentifierUseCase from '@/core/useCases/users/FetchUserByidentifierUseCase.ts'
import type { UserInput } from '@/core/domain/users/UserInput.ts'
import CreateUserUseCase from '@/core/useCases/users/CreateUserUseCase.ts'
import UpdateUserUseCase from '@/core/useCases/users/UpdateUserUseCase.ts'
import VerifyEmailUserUseCase from '@/core/useCases/users/VerifyEmailUserUseCase.ts'
import BlockUserUseCase from '@/core/useCases/users/BlockUserUseCase.ts'
import UnblockUserUseCase from '@/core/useCases/users/UnblockUserUseCase.ts'
import ResetPasswordUserUseCase from '@/core/useCases/users/ResetPasswordUserUseCase.ts'
import type UserSearchRequest from '@/core/domain/users/UserSearchRequest.ts'
import SendEmailResetPasswordUseCase from '@/core/useCases/users/SendEmailResetPasswordUseCase.ts'

export const useUsersStore = defineStore('Users', () => {
  // Utilisation de la base commune pour les stores
  const storeBase = createStoreBase([
    'fetchByIdentifier',
    'fetchByOffset',
    'createUser',
    'updateUser',
    'verifyUserEmail',
    'blockUser',
    'unblockUser',
    'resetPassword',
  ])

  // Refs
  const _byId = ref<Record<string, UserAdminView>>({})

  // Cache pour la pagination
  const _pages = ref<Record<number, string[]>>({})

  // Informations de pagination
  const _total = ref(0)
  const _pageSize = ref(50)

  // Liste des IDs aplatis des utilisateurs
  const _orderedIds = computed(() =>
    Object.keys(_pages.value)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((n) => _pages.value[n])
  )

  /**
   * Get item by index
   * @param index
   */
  function getItem(index: number): UserAdminView | undefined {
    const id = _orderedIds.value[index]
    return id ? _byId.value[id] : undefined
  }

  /**
   * Get user by id
   * @param identifier
   */
  function getUserByIdentifier(identifier: string): UserAdminView | null {
    return _byId.value[identifier] ?? null
  }

  /**
   * Getter dynamique pour le total
   */
  const getTotal = computed(() => _total.value)

  /**
   * Getter dynamique pour la taille de page
   */
  const getPageSize = computed(() => _pageSize.value)

  /**
   * Getter dynamique pour les IDs aplatis des utilisateurs
   */
  const getOrderedIds = computed(() => _orderedIds.value)

  /**
   * Vide le cache des utilisateurs.
   */
  function clearCache() {
    _byId.value = {}
    _pages.value = {}
    _total.value = 0
  }

  /**
   * Récupère les données d'un utilisateur par son identifiant.
   * @param identifier
   * @param refresh
   */
  async function fetchByIdentifier(identifier: string, refresh: boolean = false): Promise<void> {
    try {
      storeBase.resetError()
      storeBase.loading.value.fetchByIdentifier = true

      const res = await DependencyContainer.resolve(FetchUserByIdentifierUseCase).execute(
        identifier, refresh
      )
      if (isUserAdminView(res)) _byId.value[res.identifier] = res
    } catch (error) {
      console.log(error)
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.fetchByIdentifier = false
    }
  }

  /**
   * Récupère les utilisateurs par pagination avec offset et limit.
   * @param filters
   * @param offset
   * @param limit
   * @param sortBy
   */
  async function fetchByOffset(filters: UserSearchRequest, offset: number, limit: number, sortBy: string[] = ['id:asc']) {
    try {
      storeBase.resetError()
      storeBase.loading.value.fetchByOffset = true

      const res = await DependencyContainer.resolve(FetchAllUserUseCase).execute(filters,
        PaginationRequest.create({
          offset: offset,
          limit: limit,
          page: null,
          sortBy: sortBy
        })
      )

      // Hydrate le dictionnaire
      const users = res.content.filter(isUserAdminView)
      for (const u of users) {
        _byId.value[u.identifier] = u
      }

      updatePagesCache(offset, users)
      _total.value = res.totalElements
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.fetchByOffset = false
    }
  }

  /**
   * Création d'un utilisateur.
   * @param input
   */
  async function createUser(input: UserInput) {
    try {
      storeBase.resetError()
      storeBase.loading.value.createUser = true

      const res = await DependencyContainer.resolve(CreateUserUseCase).execute(input)
      if (isUserAdminView(res)) _byId.value[res.identifier] = res
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.createUser = false
    }
  }

  /**
   * Mise à jour d'un utilisateur.
   * @param input
   */
  async function updateUser(input: UserInput) {
    try {
      storeBase.resetError()
      storeBase.loading.value.updateUser = true

      const res = await DependencyContainer.resolve(UpdateUserUseCase).execute(input)
      if (isUserAdminView(res)) _byId.value[res.identifier] = res
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.updateUser = false
    }
  }

  /**
   * Marque le mail d'un utilisateur comme vérifié
   * @param identifier
   */
  async function verifyUserEmail(identifier: string) {
    try {
      storeBase.resetError()
      storeBase.loading.value.verifyUserEmail = true

      const res = await DependencyContainer.resolve(VerifyEmailUserUseCase).execute(identifier)
      if (isUserAdminView(res)) _byId.value[res.identifier] = res
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.verifyUserEmail = false
    }
  }

  /**
   * Bloque un utilisateur.
   * @param identifier
   */
  async function blockUser(identifier: string) {
    try {
      storeBase.resetError()
      storeBase.loading.value.blockUser = true

      const res = await DependencyContainer.resolve(BlockUserUseCase).execute(identifier)
      if (isUserAdminView(res)) _byId.value[res.identifier] = res
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.blockUser = false
    }
  }

  /**
   * Bloque un utilisateur.
   * @param identifier
   */
  async function unblockUser(identifier: string) {
    try {
      storeBase.resetError()
      storeBase.loading.value.unblockUser = true

      const res = await DependencyContainer.resolve(UnblockUserUseCase).execute(identifier)
      if (isUserAdminView(res)) _byId.value[res.identifier] = res
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.unblockUser = false
    }
  }

  /**
   * Reset du mot de passe de l'utilisateur
   * @param identifier
   */
  async function resetPassword(identifier: string): Promise<string | undefined> {
    try {
      storeBase.resetError()
      storeBase.loading.value.resetPassword = true

      return await DependencyContainer.resolve(ResetPasswordUserUseCase).execute(identifier)
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.resetPassword = false
    }
  }

  /**
   * Envoie un email pour reinitialiser le mot de passe
   * @param identifier
   */
  async function sendEmailResetPassword(identifier: string): Promise<boolean | undefined> {
    try {
      storeBase.resetError()
      storeBase.loading.value.sendEmailResetPassword = true

      return await DependencyContainer.resolve(SendEmailResetPasswordUseCase).execute(identifier)
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value.sendEmailResetPassword = false
    }
  }

  /**
   * Mise à jour du cache de pages à partir de l'offset
   */
  function updatePagesCache(offset: number, users: UserAdminView[]) {
    if (!users.length) return
    const size = _pageSize.value

    // Pour chaque item, calcule sa page et range-le dedans
    for (let i = 0; i < users.length; i++) {
      const absoluteIndex = offset + i // index global (0-based)
      const pageIndex = Math.floor(absoluteIndex / size)
      const indexInPage = absoluteIndex % size

      // Initialise le tableau de la page si nécessaire avec la bonne longueur
      if (!_pages.value[pageIndex]) {
        _pages.value[pageIndex] = Array<string>(size).fill(undefined as any)
      }
      _pages.value[pageIndex][indexInPage] = users[i].identifier
    }
  }

  /**
   * Réinitialise le store
   */
  function reset(): void {
    _byId.value = {}
    _pages.value = {}
    _total.value = 0
    storeBase.resetBase()
  }

  return {
    ...storeBase,
    usersStoreError: storeBase.error,
    usersStoreLoading: storeBase.loading,
    getItem,
    getUserByIdentifier,
    getPageSize,
    getTotal,
    getOrderedIds,
    clearCache,
    fetchByIdentifier,
    fetchByOffset,
    createUser,
    updateUser,
    verifyUserEmail,
    blockUser,
    unblockUser,
    resetPassword,
    sendEmailResetPassword,
    reset
  }
})